<template>
  <vue-final-modal
    :value="visible"
    name="summarize-modal"
    :classes="['summarize-modal__container']"
    :content-class="['summarize-modal__content', 'v--modal-box', 'v--modal']"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onInput"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div class="v--modal-close-top">
      <el-button @click="close" icon="el-icon-close" type="text" />
    </div>

    <div class="summarize-modal-body">
      <div class="summarize-title">文章を短くする</div>
      <p class="summarize-lead">
        入力欄をオーバーしています。目標
        <strong>{{ targetLength }}</strong> 字程度まで短縮した提案を
        <strong>{{ variationCount }}</strong> パターンご用意します。
      </p>

      <div v-if="loading" class="summarize-loading">
        <i class="el-icon-loading"></i>
        <span>AI が短縮案を作成しています…</span>
      </div>

      <div v-else-if="errorMessage" class="summarize-error">
        <i class="el-icon-warning"></i> {{ errorMessage }}
        <div class="summarize-error-actions">
          <el-button size="small" @click="fetchSummaries"
            >もう一度試す</el-button
          >
        </div>
      </div>

      <div v-else-if="summaries.length > 0" class="summarize-body">
        <div class="summarize-tabs">
          <button
            v-for="(item, index) in summaries"
            :key="index"
            class="summarize-tab"
            :class="{ 'is-active': activeIndex === index }"
            @click="activeIndex = index"
          >
            案{{ index + 1 }}
            <span class="summarize-tab-length">{{ item.length }}字</span>
          </button>
        </div>

        <div class="summarize-preview">
          <div class="summarize-meta">
            <span
              >短縮案 {{ activeIndex + 1 }}：{{ currentSummary.length }}字</span
            >
            <span class="summarize-meta-diff">
              （元: {{ originalLength }}字 / 差分 {{ diffText }}）
            </span>
          </div>
          <div class="summarize-text">{{ currentSummary.text }}</div>
        </div>

        <div class="summarize-actions">
          <el-button @click="close">キャンセル</el-button>
          <el-button type="primary" @click="applyCurrent"
            >この案を採用</el-button
          >
        </div>
      </div>

      <div v-else class="summarize-empty">
        <i class="el-icon-info"></i>
        短縮案を生成できませんでした。文章を少し編集してから再度お試しください。
        <div class="summarize-error-actions">
          <el-button @click="close">閉じる</el-button>
        </div>
      </div>
    </div>
  </vue-final-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue'
import { buildSummarizeFetcher } from './summarize-api'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'

type SummaryItem = { text: string; length: number }

const props = withDefaults(
  defineProps<{
    visible: boolean
    text?: string
    targetLength: number
    variationCount?: number
    context?: string
  }>(),
  {
    text: '',
    variationCount: 3,
    context: '文章'
  }
)

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', text: string): void
}>()

const loading = ref(false)
const errorMessage = ref('')
const summaries = ref<SummaryItem[]>([])
const activeIndex = ref(0)
const abortController = ref<AbortController | null>(null)
const applied = ref(false)

const { user } = useAuth()
const { varuna } = useHostServices()
const vm = getCurrentInstance()!.proxy as unknown as {
  $axios: {
    post: (
      url: string,
      data: unknown,
      config?: { timeout?: number }
    ) => Promise<unknown>
    isCancel?: (err: unknown) => boolean
  }
}

const originalLength = computed(() => (props.text || '').length)
const currentSummary = computed(
  () => summaries.value[activeIndex.value] || { text: '', length: 0 }
)
const diffText = computed(() => {
  const d = currentSummary.value.length - originalLength.value
  if (d === 0) return '±0字'
  return (d > 0 ? '+' : '') + d + '字'
})

function onInput(value: boolean) {
  emit('update:visible', value)
}

function close() {
  cancelInflight()
  emit('update:visible', false)
}

function onOpened() {
  activeIndex.value = 0
  errorMessage.value = ''
  summaries.value = []
  applied.value = false
  fetchSummaries()
}

function onClosed() {
  cancelInflight()
  if (!applied.value && summaries.value.length > 0) {
    reportEvent('dismiss')
  }
}

function cancelInflight() {
  if (abortController.value) {
    abortController.value.abort()
    abortController.value = null
  }
  loading.value = false
}

function fetchSummaries() {
  cancelInflight()
  errorMessage.value = ''
  summaries.value = []

  if (!props.text || props.text.length < 50) {
    errorMessage.value = '文章が短いため、短縮提案の対象外です。'
    return
  }
  if (props.targetLength <= 0 || props.targetLength >= props.text.length) {
    errorMessage.value = '目標字数が不正です。'
    return
  }

  const fetcher = buildSummarizeFetcher(vm)
  if (!fetcher) {
    errorMessage.value =
      '短縮機能が利用できません。時間をおいて再度お試しください。'
    return
  }

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null
  abortController.value = controller
  loading.value = true

  fetcher({
    text: props.text,
    targetLength: props.targetLength,
    variationCount: props.variationCount,
    context: props.context,
    signal: controller ? controller.signal : null
  })
    .then((next: SummaryItem[]) => {
      if (abortController.value !== controller) return
      abortController.value = null
      loading.value = false
      summaries.value = Array.isArray(next) ? next : []
      activeIndex.value = 0
    })
    .catch((err: { response?: { status?: number }; code?: string }) => {
      if (abortController.value !== controller) return
      abortController.value = null
      loading.value = false
      if (vm.$axios.isCancel?.(err)) return
      const status = err && err.response && err.response.status
      if (status === 401 || err.code === 'UNAUTHENTICATED') {
        errorMessage.value = 'ログインが必要です。'
      } else if (status === 403) {
        errorMessage.value = 'ヤギプライム会員限定機能です。'
      } else if (status === 429) {
        errorMessage.value =
          '本日の利用回数上限に達しました。明日またお試しください。'
      } else if (status === 502) {
        errorMessage.value =
          'AIサービスが一時的に利用できません。しばらくしてから再度お試しください。'
      } else {
        errorMessage.value =
          '短縮案の取得に失敗しました。しばらくしてから再度お試しください。'
      }
    })
}

function applyCurrent() {
  const picked = currentSummary.value
  if (!picked || !picked.text) return
  applied.value = true
  reportEvent('accept', { candidateIndex: activeIndex.value })
  emit('apply', picked.text)
  emit('update:visible', false)
}

function reportEvent(action: string, extra?: { candidateIndex?: number }) {
  const uid = user.value?.uid
  const endpoint = varuna.endPoints.proofreadEvent
  if (!uid || !endpoint) return
  const payload: Record<string, unknown> = {
    uuid: uid,
    feature: 'summarize',
    action
  }
  if (extra && typeof extra.candidateIndex === 'number') {
    payload.candidate_index = extra.candidateIndex
  }
  try {
    vm.$axios.post(endpoint, payload, { timeout: 5000 }).catch(() => {})
  } catch {
    /* noop */
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/format-edit/vfm-attach-body-fixed';
@include vfm-root-fixed-when-has('.summarize-modal__container');

.summarize-modal__container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.summarize-modal__content.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  flex: 0 1 auto;
  width: 90%;
  max-width: 680px;
  height: auto;
  margin: 0 auto;
  padding: 0;
  left: 0 !important;
}

.summarize-modal__content .v--modal-close-top {
  text-align: right;
  padding: 8px 12px 0;
}
</style>

<style scoped>
.summarize-modal-body {
  padding: 8px 20px 20px;
}

.summarize-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
}

.summarize-lead {
  font-size: 13px;
  color: #666;
  margin: 0 0 16px;
}

.summarize-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #888;
  padding: 24px 0;
  justify-content: center;
}

.summarize-loading i {
  font-size: 20px;
}

.summarize-error {
  color: #c0392b;
  background: #fdecea;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 14px;
}

.summarize-error-actions {
  margin-top: 10px;
  text-align: right;
}

.summarize-empty {
  padding: 18px 0;
  color: #666;
  font-size: 14px;
  text-align: center;
}

.summarize-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summarize-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summarize-tab {
  flex: 1 1 0;
  min-width: 90px;
  padding: 10px 8px;
  border: 1px solid #d0d0d0;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #444;
  transition: all 0.15s;
}

.summarize-tab:hover {
  border-color: #3b82f6;
}

.summarize-tab.is-active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.summarize-tab-length {
  display: block;
  font-size: 11px;
  margin-top: 2px;
  opacity: 0.85;
}

.summarize-preview {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 14px;
  background: #fafafa;
}

.summarize-meta {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summarize-meta-diff {
  color: #3b82f6;
}

.summarize-text {
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.7;
  color: #222;
  max-height: 260px;
  overflow-y: auto;
}

.summarize-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
</style>
